import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PlanSelector from '../components/PlanSelector';
import { authApi } from '../lib/api';
import { formatApiErrorDetail } from '../lib/utils';
import {
  formatSelectedPlanLabel,
  normalizePlanSelection,
  readCurrencyFromSearch,
} from '../lib/pricingCatalog';
import { emailErrorMessage, phoneErrorMessage, validateEmailParts, validatePhoneParts } from '../lib/contactValidation';
import {
  PASSWORD_POLICY_HINT,
  passwordErrorMessage,
  validatePasswordParts,
} from '../lib/passwordValidation';

const LOGO_FULL = '/images/logo-full.png';

function readPlanFromSearch(search) {
  const q = new URLSearchParams(search || '');
  return normalizePlanSelection(q.get('plan'), q.get('cycle'));
}

export default function CompanyRegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const initial = useMemo(() => readPlanFromSearch(location.search), [location.search]);
  const currency = readCurrencyFromSearch(location.search);
  const [selectedPlan, setSelectedPlan] = useState(initial.plan);
  const [selectedCycle, setSelectedCycle] = useState(initial.cycle);
  const [apiPlans, setApiPlans] = useState([]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationGstin, setOrganizationGstin] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.publicPlans(currency);
        if (!cancelled) setApiPlans(res.data?.plans || []);
      } catch {
        if (!cancelled) setApiPlans([]);
      }
    })();
    return () => { cancelled = true; };
  }, [currency]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.get('plan') === selectedPlan &&
      params.get('cycle') === selectedCycle &&
      (params.get('currency') || 'INR').toUpperCase() === currency
    ) {
      return;
    }
    params.set('plan', selectedPlan);
    params.set('cycle', selectedCycle);
    params.set('currency', currency);
    navigate({ pathname: '/register/company', search: params.toString() }, { replace: true });
  }, [selectedPlan, selectedCycle, currency, location.search, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const emailCheck = validateEmailParts(email);
      if (!emailCheck.valid) {
        setError(emailErrorMessage(emailCheck, t));
        setLoading(false);
        return;
      }
      const nameClean = name.trim().replace(/\s+/g, ' ');
      const orgClean = organizationName.trim().replace(/\s+/g, ' ');
      if (!nameClean || nameClean.length > 120) {
        setError('Full name is required (max 120 characters)');
        setLoading(false);
        return;
      }
      if (!orgClean || orgClean.length > 120) {
        setError('Organization name is required (max 120 characters)');
        setLoading(false);
        return;
      }
      const phoneCheck = validatePhoneParts(phone, { required: false });
      if (!phoneCheck.valid) {
        setError(phoneErrorMessage(phoneCheck, t));
        setLoading(false);
        return;
      }
      const passwordCheck = validatePasswordParts(password);
      if (!passwordCheck.valid) {
        setError(passwordErrorMessage(passwordCheck, t));
        setLoading(false);
        return;
      }
      const res = await authApi.registerCompany({
        name: nameClean,
        email: emailCheck.email,
        password: passwordCheck.password,
        organization_name: orgClean,
        organization_gstin: organizationGstin || undefined,
        phone: phoneCheck.phone || undefined,
        subscription_plan: selectedPlan === 'free' ? 'free' : selectedPlan,
        billing_cycle: selectedCycle,
        currency,
      });
      setDone(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t('auth.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const planLabel = formatSelectedPlanLabel(selectedPlan, selectedCycle, currency);

  return (
    <div className="min-h-screen flex flex-col justify-center bg-white px-8 sm:px-12 py-10 overflow-y-auto">
      <div className="w-full max-w-lg mx-auto">
          <div className="mb-8">
            <img src={LOGO_FULL} alt="OMNICHEK" className="h-10 object-contain" />
          </div>

          {done ? (
            <div className="space-y-4">
              <h1 className="text-2xl font-heading font-semibold text-zinc-900">
                {t('auth.verifyEmailCheckInbox')}
              </h1>
              <p className="text-zinc-600 text-sm">
                {done.message || t('auth.verifyEmailCheckInboxDesc')}
              </p>
              {done.verify_link && (
                <div className="p-4 bg-brand-50 border border-brand-200 rounded-md text-sm">
                  <p className="font-medium text-brand-900 mb-2">{t('auth.verifyEmailDevLink')}</p>
                  <a href={done.verify_link} className="text-brand-700 break-all hover:underline">
                    {done.verify_link}
                  </a>
                </div>
              )}
              <Button className="w-full" onClick={() => navigate('/login')}>
                {t('auth.backToSignIn')}
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-heading font-semibold text-zinc-900 mb-2">
                {t('auth.companyRegisterTitle')}
              </h1>
              <p className="text-zinc-500 mb-6">{t('auth.companyRegisterDesc')}</p>

              <div className="mb-6 p-3 rounded-lg bg-zinc-50 border border-zinc-200 text-sm text-zinc-700">
                {t('auth.selectedPlan')}: <span className="font-semibold">{planLabel}</span>
              </div>

              <PlanSelector
                currency={currency}
                selectedPlan={selectedPlan}
                selectedCycle={selectedCycle}
                onSelectPlan={(plan, cycle) => {
                  setSelectedPlan(plan);
                  setSelectedCycle(cycle);
                }}
                apiPlans={apiPlans}
                compact
              />

              {error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">{t('auth.orgName')}</Label>
                  <Input
                    id="org-name"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Your Company Inc."
                    required
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="full-name">{t('auth.fullName')}</Label>
                  <Input
                    id="full-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('auth.phoneOptional')}</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="9876543210"
                    className="h-11"
                  />
                  <p className="text-xs text-zinc-500">{t('validation.phoneInvalid')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gstin">{t('auth.gstinOptional')}</Label>
                  <Input
                    id="gstin"
                    value={organizationGstin}
                    onChange={(e) => setOrganizationGstin(e.target.value)}
                    placeholder="22AAAAA0000A1Z5"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('common.password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
                      required
                      minLength={10}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                    >
                      {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white font-medium"
                >
                  {loading ? (
                    <CircleNotch className="w-5 h-5 animate-spin" />
                  ) : (
                    t('auth.createAccount')
                  )}
                </Button>
              </form>

              <p className="mt-8 text-center text-zinc-500 text-sm">
                {t('auth.haveAccount')}{' '}
                <Link to="/login" className="text-brand-600 font-medium hover:underline">
                  {t('auth.signIn')}
                </Link>
              </p>
            </>
          )}
        </div>
    </div>
  );
}
